import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateMachineTypeMutation, useUpdateMachineTypeMutation } from '../../../../hooks/mutations/useMachineTypeMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { MachineType } from '../../../../services/apiSLTP/materials/machinesTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { crudStatus, getPriceTypeInfos, priceTypeEnum, systemConfigKeyEnum } from '../../../../utils/enums';
import CustomTextInput from '../../../CustomTextInput';
import DropDown from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody from '../../ModalBody';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface ModalProps {
    machineType: MachineType | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateMachineTypeModal: React.FC<ModalProps> = ({ machineType, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [selectedDesignation, setSelectedDesignation] = useState(machineType?.designation || '');
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(machineType?.crudStatus || crudStatus.OK);
    const [price, setPrice] = useState(typeof machineType?.price === 'number' ? machineType?.price.toString() : '');
    const [priceType, setPriceType] = useState({
        label: capitalizeFirstLetter(t(getPriceTypeInfos(machineType?.priceType || priceTypeEnum.DAY))),
        value: (machineType?.priceType || priceTypeEnum.DAY).toString(),
    });
    const [displayOrder, setDisplayOrder] = useState(typeof machineType?.displayOrder === 'number' ? machineType?.displayOrder.toString() : '');

    const { mutate: mutateMachineType } = useUpdateMachineTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating machineType:', error.message),
    });

    const { mutate: addMachineType } = useCreateMachineTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding machineType:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatus.OK : crudStatus.INACTIVE);
    };

    const isFormValid = () => {
        return !!(selectedDesignation.length && price.length);
    };

    const handleSubmit = () => {
        if (isEditMode) {
            mutateMachineType({
                machineTypeId: machineType?._id || '',
                data: {
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                },
            });
        } else {
            if (priceType)
                addMachineType({
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                });
        }
    };

    return (
        <ModalBody
            title={t('machine')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!isFormValid()}
            handleOk={handleSubmit}
        >
            <CustomTextInput value={selectedDesignation} onChange={setSelectedDesignation} label={t('name')} errorText={t('error')} required />
            <ToggleLine checked={selectedCrudStatus === crudStatus.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                required
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    if (value) setPriceType({ value: value?.value, label: value?.label });
                }}
                inputLabel={t('pricing')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateMachineTypeModal;
